import ThemeModal from "components/Common/ThemeModal"
import { Badge, Col, Row, Spinner } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"
import ReversalBadge from "pages/AccountPages/utils/ReversalBadge"
import ReversalStatusBanner from "pages/AccountPages/utils/ReversalStatusBanner"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const ReceiptRow = ({ label, value, mono = false, strong = false }) => {
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
      <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
      <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
        {value || "-"}
      </span>
    </div>
  )
}

const ReceiptSection = ({ title, children }) => {
  return (
    <div className="pt-3">
      <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
      {children}
    </div>
  )
}

const AccountLoanRepaymentView = ({ modal, toggleModal, repayment, loading }) => {
  const borrowerName = getEntityLabel(repayment?.loan?.borrower, repayment?.loan?.borrower_id, "Borrower")
  const loanId = repayment?.loan?.id || repayment?.loan_id || "-"
  const accountName = getEntityLabel(repayment?.account, repayment?.account_id, "Account")
  const paymentMethodName = getEntityLabel(repayment?.payment_method || repayment?.paymentMethod, repayment?.payment_method_id, "Method")
  const createdBy = getUserLabel(repayment?.createdBy || repayment?.created_by)
  const updatedBy = getUserLabel(repayment?.updatedBy || repayment?.updated_by)

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Loan Repayment Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !repayment ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          <ReversalStatusBanner
            isReversed={repayment.is_reversed}
            reversedText="This repayment has been reversed and no longer counts toward the recovered loan balance."
          />
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>
            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Loan Repayment Receipt</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>ALR-{String(repayment.id ?? "0000").padStart(4, "0")}</div>
            </div>
            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(8, 145, 178, 0.14), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Loan repayment</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>{borrowerName}</div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color="primary" pill className="px-2 py-1">Credited</Badge>
                    <ReversalBadge isReversed={repayment.is_reversed} className="px-2 py-1" />
                    <span className="text-muted small font-monospace">#{repayment.id ?? "-"}</span>
                    <span className="text-muted small">{repayment.repayment_date ? formatReadableDate(repayment.repayment_date) : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Amount</div>
                  <div className="fw-bold text-dark" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>{getMoneyDisplayValue(repayment.amount)}</div>
                  <div className="text-muted small mt-1">via {paymentMethodName}</div>
                </div>
              </div>
            </div>
            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>
            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Receipt Details">
                    <ReceiptRow label="Loan ID" value={loanId} mono />
                    <ReceiptRow label="Account" value={accountName} />
                    <ReceiptRow label="Repayment Date" value={repayment.repayment_date ? formatReadableDate(repayment.repayment_date) : "-"} />
                    <ReceiptRow label="Method" value={paymentMethodName} />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Related Entities">
                    <ReceiptRow label="Borrower" value={borrowerName} />
                    <ReceiptRow label="Loan Reference" value={`Loan #${loanId}`} mono />
                    <ReceiptRow label="Record ID" value={repayment.id ?? "-"} mono />
                    <ReceiptRow label="Account" value={accountName} />
                  </ReceiptSection>
                </Col>
              </Row>
            </div>
            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Payment Ledger">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Description" value="Loan repayment credited" />
                  <ReceiptRow label="Channel" value={paymentMethodName} />
                  <ReceiptRow label="Allocation Account" value={accountName} />
                  <ReceiptRow label="Total Credited" value={getMoneyDisplayValue(repayment.amount)} strong mono />
                </div>
              </ReceiptSection>
            </div>
            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Notes">
                <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  {repayment.notes || "No notes added for this repayment."}
                </div>
              </ReceiptSection>
            </div>
            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{repayment.created_at ? formatReadableDate(repayment.created_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Updated</div>
                  <div className="small text-dark">{updatedBy}</div>
                  <div className="small text-muted">{repayment.updated_at ? formatReadableDate(repayment.updated_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Status</div>
                  <div className="small text-dark fw-semibold">{repayment.is_reversed ? "Reversed" : repayment.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{repayment.is_reversed ? "Cancelled by a ledger reversal entry" : repayment.deleted_at ? formatReadableDate(repayment.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountLoanRepaymentView
